'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { ChevronLeft, ChevronRight, Maximize2, Play, Box, Camera, X } from 'lucide-react';

const GALLERY_IMAGES = [
{ id: 'gal-001', src: "https://img.rocket.new/generatedImages/rocket_gen_img_1e911804b-1772842485480.png", alt: 'Geniş salon, modern mobilyalar, deniz manzarası, ahşap zemin', label: 'Salon' },
{ id: 'gal-002', src: "https://img.rocket.new/generatedImages/rocket_gen_img_157c86a07-1772792480908.png", alt: 'Modern açık mutfak, ada tezgah, paslanmaz çelik aletler', label: 'Mutfak' },
{ id: 'gal-003', src: "https://img.rocket.new/generatedImages/rocket_gen_img_19fd64a92-1773164991403.png", alt: 'Ana yatak odası, kral boyutu yatak, pencere manzarası', label: 'Yatak Odası' },
{ id: 'gal-004', src: "https://images.unsplash.com/photo-1722349670498-4f24a0bb79e8", alt: 'Lüks banyo, mermer tezgah, duş kabini, modern armatürler', label: 'Banyo' },
{ id: 'gal-005', src: "https://img.rocket.new/generatedImages/rocket_gen_img_1a641677e-1774283633841.png", alt: 'Geniş balkon, deniz manzarası, dış mekan mobilyaları', label: 'Balkon' },
{ id: 'gal-006', src: "https://images.unsplash.com/photo-1591529865762-f84b1490ef8a", alt: 'Çocuk odası, renkli dekorasyon, yerleşik dolap', label: 'Çocuk Odası' },
{ id: 'gal-007', src: "https://images.unsplash.com/photo-1665111911781-355099b89fa0", alt: 'Bina dış cephesi, modern mimari, giriş holü', label: 'Dış Cephe' },
{ id: 'gal-008', src: "https://images.unsplash.com/photo-1729606457764-e27f669fae1f", alt: 'Giriş holü, özel asansör, modern dekorasyon', label: 'Giriş' }];


export default function PropertyGallery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeMedia, setActiveMedia] = useState<'photos' | '3d' | 'video'>('photos');

  const prev = () => setActiveIndex((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  const next = () => setActiveIndex((i) => (i + 1) % GALLERY_IMAGES.length);

  return (
    <>
      <div className="rounded-2xl overflow-hidden bg-foreground">
        {/* Media Type Tabs */}
        <div className="flex items-center gap-1 p-3 bg-foreground/90">
          {[
          { id: 'photos', icon: <Camera size={14} />, label: `Fotoğraflar (${GALLERY_IMAGES.length})` },
          { id: 'video', icon: <Play size={14} />, label: 'Video' },
          { id: '3d', icon: <Box size={14} />, label: '3D Tur' }].
          map((tab) =>
          <button
            key={`media-tab-${tab.id}`}
            onClick={() => setActiveMedia(tab.id as typeof activeMedia)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeMedia === tab.id ? 'bg-white text-foreground' : 'text-white/70 hover:text-white hover:bg-white/10'}`}>
            
              {tab.icon}
              {tab.label}
            </button>
          )}
          <div className="ml-auto flex items-center gap-1">
            <span className="text-white/60 text-xs">{activeIndex + 1} / {GALLERY_IMAGES.length}</span>
          </div>
        </div>

        {/* Main Image */}
        <div className="relative aspect-[16/9] overflow-hidden group">
          {activeMedia === 'photos' &&
          <>
              <AppImage
              src={GALLERY_IMAGES[activeIndex].src}
              alt={GALLERY_IMAGES[activeIndex].alt}
              width={1200}
              height={675}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              priority={activeIndex === 0} />
            
              {/* Room label */}
              <div className="absolute top-4 left-4 px-3 py-1.5 rounded-lg bg-black/50 text-white text-xs font-semibold">
                {GALLERY_IMAGES[activeIndex].label}
              </div>

              {/* Nav Arrows */}
              <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
              
                <ChevronLeft size={20} />
              </button>
              <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
              
                <ChevronRight size={20} />
              </button>

              {/* Fullscreen */}
              <button
              onClick={() => setIsLightboxOpen(true)}
              className="absolute bottom-3 right-3 w-9 h-9 rounded-xl bg-black/50 hover:bg-black/80 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100">
              
                <Maximize2 size={16} />
              </button>
            </>
          }

          {activeMedia === 'video' &&
          <div className="w-full h-full bg-foreground flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
                <Play size={28} className="text-white ml-1" />
              </div>
              <p className="text-white/70 text-sm font-medium">Drone & İç Mekan Video</p>
              <p className="text-white/40 text-xs">3:24 · 4K Ultra HD</p>
              {/* Backend integration point: Video player using HLS/MP4 from media storage */}
            </div>
          }

          {activeMedia === '3d' &&
          <div className="w-full h-full bg-gradient-to-br from-slate-900 to-primary flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                <Box size={28} className="text-white" />
              </div>
              <p className="text-white font-bold">3D Sanal Tur</p>
              <p className="text-white/60 text-sm text-center max-w-xs">Three.js ile oluşturulmuş 3D model. Tüm odaları gezin, ölçüm alın.</p>
              <button className="btn-primary text-sm">
                3D Turu Başlat
              </button>
              {/* Backend integration point: Three.js / GLTF model loader */}
            </div>
          }
        </div>

        {/* Thumbnails */}
        <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide bg-foreground/90">
          {GALLERY_IMAGES.map((img, i) =>
          <button
            key={img.id}
            onClick={() => {setActiveIndex(i);setActiveMedia('photos');}}
            className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${activeIndex === i && activeMedia === 'photos' ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}>
            
              <AppImage
              src={img.src}
              alt={img.alt}
              width={64}
              height={48}
              className="w-full h-full object-cover" />
            
            </button>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen &&
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in">
          <button
          onClick={() => setIsLightboxOpen(false)}
          className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all">
          
            <X size={20} />
          </button>
          <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all">
            <ChevronLeft size={24} />
          </button>
          <div className="max-w-5xl max-h-[85vh] mx-16">
            <img
            src={GALLERY_IMAGES[activeIndex].src}
            alt={GALLERY_IMAGES[activeIndex].alt}
            className="max-w-full max-h-full object-contain rounded-xl" />
          
            <p className="text-white/60 text-sm text-center mt-3">{GALLERY_IMAGES[activeIndex].label} · {activeIndex + 1}/{GALLERY_IMAGES.length}</p>
          </div>
          <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all">
            <ChevronRight size={24} />
          </button>
        </div>
      }
    </>);

}